import React from 'react';
import { motion } from 'framer-motion';

// Logo Concept 1: Shield with Circuit Pattern
export const ShieldLogo = ({ size = 'default', className = '' }) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-12 h-12',
    large: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  return (
    <div className={`${sizeClasses[size]} ${className}`}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Shield Background */}
        <path
          d="M50 5 L85 25 L85 60 C85 75 70 85 50 95 C30 85 15 75 15 60 L15 25 Z"
          fill="#1e3a8a"
          stroke="#3b82f6"
          strokeWidth="2"
        />
        
        {/* Circuit Pattern */}
        <path
          d="M25 35 L35 35 L35 45 L45 45 L45 35 L55 35 L55 45 L65 45 L65 35 L75 35"
          stroke="#06b6d4"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
        
        {/* Central Element */}
        <circle cx="50" cy="50" r="8" fill="#f59e0b" />
        <circle cx="50" cy="50" r="4" fill="white" />
      </svg>
    </div>
  );
};

// Logo Concept 2: Simple DMS
export const DMSLogo = ({ size = 'default', className = '' }) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-12 h-12',
    large: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  return (
    <div className={`${sizeClasses[size]} ${className}`}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Background Circle */}
        <circle cx="50" cy="50" r="45" fill="#1e40af" />
        
        {/* D Letter */}
        <path
          d="M25 20 L25 80 L45 80 C60 80 70 70 70 50 C70 30 60 20 45 20 Z"
          fill="white"
        />
        
        {/* M Letter */}
        <path
          d="M75 20 L75 80 L65 80 L55 50 L45 80 L35 80 L25 50 L15 80 L5 80 L5 20 L15 20 L25 50 L35 20 L45 50 L55 20 L65 20 Z"
          fill="white"
          transform="translate(30, 0)"
        />
        
        {/* S Letter */}
        <path
          d="M85 20 C95 20 95 30 90 35 C95 40 95 50 85 50 C75 50 75 40 80 35 C75 30 75 20 85 20 Z"
          fill="white"
          transform="translate(60, 0)"
        />
      </svg>
    </div>
  );
};

// Logo Concept 3: Minimalist Shield
export const MinimalistLogo = ({ size = 'default', className = '', showText = false }) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-12 h-12',
    large: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div className={sizeClasses[size]}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Shield */}
          <path
            d="M50 10 L80 25 L80 60 C80 80 65 90 50 90 C35 90 20 80 20 60 L20 25 Z"
            fill="#1e293b"
            stroke="#3b82f6"
            strokeWidth="1.5"
          />
          
          {/* Central Element */}
          <circle cx="50" cy="50" r="12" fill="#f59e0b" />
          <circle cx="50" cy="50" r="6" fill="white" />
        </svg>
      </div>
      
      {showText && (
        <div className="ml-3">
          <div className="text-xl font-bold text-white">DMS</div>
          <div className="text-xs text-gray-400">Dark Matter Solutions</div>
        </div>
      )}
    </div>
  );
};

// Logo Concept 4: Simple Animated Logo
export const AnimatedLogo = ({ size = 'default', className = '' }) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-12 h-12',
    large: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  return (
    <motion.div 
      className={`${sizeClasses[size]} ${className}`}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Shield */}
        <path
          d="M50 15 L75 30 L75 60 C75 80 65 85 50 85 C35 85 25 80 25 60 L25 30 Z"
          fill="#1e293b"
          stroke="#3b82f6"
          strokeWidth="2"
        />
        
        {/* Central Element */}
        <circle cx="50" cy="50" r="15" fill="#f59e0b" />
      </svg>
    </motion.div>
  );
};

// Logo Concept 5: Text-based Logo
export const TextLogo = ({ size = 'default', className = '', variant = 'default' }) => {
  const sizeClasses = {
    small: 'text-lg',
    default: 'text-2xl',
    large: 'text-3xl',
    xl: 'text-4xl'
  };

  const variants = {
    default: (
      <div className={`font-black tracking-tight ${sizeClasses[size]} ${className}`}>
        <span className="text-white">DARK MATTER</span>
        <br />
        <span className="text-blue-400">SOLUTIONS</span>
      </div>
    ),
    compact: (
      <div className={`font-bold tracking-wide ${sizeClasses[size]} ${className}`}>
        <span className="text-blue-400">DMS</span>
      </div>
    ),
    minimal: (
      <div className={`font-semibold ${sizeClasses[size]} ${className}`}>
        <span className="text-white">Dark Matter</span>
        <span className="text-blue-400">.</span>
      </div>
    )
  };

  return variants[variant];
};

// NEW: Hexagonal Dark Matter Logo (User's Exact Design)
export const HexagonalLogo = ({ size = 'default', className = '', variant = 'default', animated = false }) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-12 h-12',
    large: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  const variants = {
    default: {
      // Full logo using actual logo file
      icon: (
        <img 
          src="/FullLogo_Transparent_NoBuffer.png" 
          alt="Dark Matter Solutions" 
          className="w-full h-full object-contain"
        />
      ),
      text: null
    },
    iconOnly: {
      // Just the icon part of the logo
      icon: (
        <img 
          src="/FullLogo_Transparent_NoBuffer.png" 
          alt="Dark Matter Solutions" 
          className="w-full h-full object-contain"
          style={{ objectPosition: 'left center' }}
        />
      ),
      text: null
    },
    textOnly: {
      // Just the text - exact gradient from user's design
      icon: null,
      text: (
        <div className="text-center">
          <div className="font-black tracking-tight">
            <span className="bg-gradient-to-r from-cyan-500 to-blue-700 bg-clip-text text-transparent">
              DARK MATTER
            </span>
          </div>
          <div className="text-blue-800 font-bold">SOLUTIONS</div>
        </div>
      )
    },
    compact: {
      // Compact version
      icon: (
        <svg viewBox="0 0 40 40" className="w-full h-full">
          <path
            d="M20 8 L30 15 L30 25 L20 32 L10 25 L10 15 Z"
            fill="url(#hexGradient1)"
            stroke="url(#hexGradient1)"
            strokeWidth="0.5"
          />
          <path
            d="M20 19 L25 22 L25 26 L20 29 L15 26 L15 22 Z"
            fill="url(#hexGradient2)"
          />
        </svg>
      ),
      text: (
        <div className="text-center">
          <div className="font-bold text-white">DMS</div>
        </div>
      )
    }
  };

  const selectedVariant = variants[variant] || variants.default;
  const LogoIcon = selectedVariant.icon;
  const LogoText = selectedVariant.text;

  const containerClass = variant === 'textOnly' ? 'flex items-center justify-center' : 'flex items-center space-x-3';

  return (
    <motion.div 
      className={`${containerClass} ${className}`}
      whileHover={animated ? { scale: 1.05 } : {}}
      whileTap={animated ? { scale: 0.95 } : {}}
      transition={animated ? { type: "spring", stiffness: 300 } : {}}
    >
      {LogoIcon && (
        <div className={sizeClasses[size]}>
          {LogoIcon}
        </div>
      )}
      {LogoText && (
        <div className={sizeClasses[size]}>
          {LogoText}
        </div>
      )}
    </motion.div>
  );
};

// Logo Selector Component
export const LogoSelector = ({ onSelect, selectedLogo = 'hexagonal' }) => {
  const logos = [
    { id: 'hexagonal', name: 'Hexagonal Dark Matter', component: HexagonalLogo, color: '#06b6d4' },
    { id: 'shield', name: 'Shield with Circuit', component: ShieldLogo, color: '#1e3a8a' },
    { id: 'dms', name: 'DMS Geometric', component: DMSLogo, color: '#1e40af' },
    { id: 'minimalist', name: 'Minimalist Shield', component: MinimalistLogo, color: '#1e293b' },
    { id: 'animated', name: 'Animated Logo', component: AnimatedLogo, color: '#1e293b' },
    { id: 'text', name: 'Text Logo', component: TextLogo, color: '#3b82f6' }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
      {logos.map((logo) => {
        const LogoComponent = logo.component;
        return (
          <motion.div
            key={logo.id}
            className={`p-6 bg-gray-800 rounded-xl border-2 cursor-pointer transition-all ${
              selectedLogo === logo.id 
                ? 'border-blue-500 bg-blue-500/10' 
                : 'border-gray-700 hover:border-gray-600'
            }`}
            onClick={() => onSelect(logo.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="text-center mb-4">
              <LogoComponent size="large" className="mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-white text-center">{logo.name}</h3>
            {selectedLogo === logo.id && (
              <div className="text-center mt-2">
                <span className="text-blue-400 text-sm">✓ Selected</span>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

export default HexagonalLogo;
